import { UploadFileFiled } from '@/features/analytics/invite-links/ui/Marking/components/UploadFileFiled'
import Icon from '@/features/analytics/invite-links/ui/Marking/icon'
import { useInviteLinksStore } from '@/features/analytics/model'
import { AnalyticsTableRowType } from '@/features/analytics/types'
import {
    apiQueryKey,
    useGetInvoices,
    useGetLinkedLinksByContract,
    useUpdateMarkingFormData,
    useUploadInvoiceFile,
} from '@/shared/api'
import {
    Checkbox,
    DisableTooltip,
    GreyRubleIconInline,
    Input2,
} from '@/shared/ui'
import { Label } from '@/shared/ui/label'
import { DocumentRecord, ORDMarkingFormData } from '@synapex/prisma/client'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

const { getWwld } = workspaceWithLinkMethods

type InvoiceProps = {
    markingFormData: ORDMarkingFormData
    analyticsTableRowType: AnalyticsTableRowType
    linkedLinksCount: number
    openLinkedLinksModal: () => void
    isLinkDeleted: boolean
} & WorkspaceWithLinkDescriptor

const Invoice: React.FC<InvoiceProps> = ({
    markingFormData,
    analyticsTableRowType,
    linkedLinksCount,
    openLinkedLinksModal,
    isLinkDeleted,
    ...props
}) => {
    const queryClient = useQueryClient()

    const { setContractError } = useInviteLinksStore()

    const [accountDetailsValue, setAccountDetailsValue] = useState(
        markingFormData.account_details || ''
    )

    const [selectedItem, setSelectedItem] = useState<null | DocumentRecord>(
        null
    )

    const uploadInvoiceFile = useUploadInvoiceFile(props)

    const { data: invoices } = useGetInvoices(
        {
            ...props,
            contragentDistributorId:
                markingFormData.ord_contragent_id!,
        },
        {
            enabled: !!markingFormData.ord_contragent_id,
        }
    )

    const contractType = markingFormData.custom_contract_id ? 'custom' : 'draft'

    const { data: linkedLinks } = useGetLinkedLinksByContract(
        props,
        markingFormData.draft_contract_id!,
        contractType,
        {
            enabled: !!markingFormData.draft_contract_id,
        }
    )

    const updateMarkingFormData = useUpdateMarkingFormData(props, {
        onSettled: (_, _e, updateMarkingFormDataDto) => {
            if (updateMarkingFormDataDto.vat_percent !== undefined) {
                for (const ll of linkedLinks?.links ?? []) {
                    void queryClient.invalidateQueries({
                        queryKey: apiQueryKey.markingFormData(getWwld(ll)),
                    })
                }

                const documentId = markingFormData.draft_contract_id
                void queryClient.invalidateQueries({
                    queryKey: apiQueryKey.linkedLinksByContract(
                        props,
                        contractType,
                        documentId
                    ),
                })
            }
        },
    })

    function handleChangeCheckboxStatus() {
        updateMarkingFormData.mutate(
            {
                invoice_flag: !markingFormData.invoice_flag,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({
                        queryKey: apiQueryKey.markingFormData(),
                        predicate: (query) => {
                            if (!query.state.data) {
                                return false
                            }
                            const queryData = query.state.data as {
                                draft_contract_id?: number
                            }
                            return (
                                queryData.draft_contract_id ===
                                markingFormData.draft_contract_id
                            )
                        },
                    })
                },
            }
        )
    }

    const handleDelete = () => {
        updateMarkingFormData.mutate(
            {
                invoice_id: null,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({
                        queryKey: apiQueryKey.markingFormData(),
                        predicate: (query) => {
                            const queryData = query.state.data as {
                                draft_contract_id?: number
                            }
                            return (
                                queryData?.draft_contract_id ===
                                markingFormData.draft_contract_id
                            )
                        },
                    })
                },
            }
        )
    }

    const handleFileChange = (file: File) => {
        if (
            !markingFormData.draft_contract_id &&
            !markingFormData.custom_contract_id
        ) {
            setContractError(true)
            return toast.error('Сначала выберите договор')
        }

        uploadInvoiceFile.mutate({
            fileName: file.name,
            file,
            ordContragentId: markingFormData.ord_contragent_id!,
            draftContractId: markingFormData.draft_contract_id!,
            customContractId: markingFormData.custom_contract_id!,
        })
    }

    const onUpdateAccountDetails = () => {
        // only update if value changed
        if (markingFormData.account_details !== accountDetailsValue) {
            updateMarkingFormData.mutate({
                account_details: accountDetailsValue,
            })
        }
    }

    useEffect(() => {
        if (markingFormData.invoice_id && invoices?.length) {
            setSelectedItem(
                invoices.find(
                    (invoice) =>
                        invoice.documentRecordId === markingFormData.invoice_id
                )!
            )
        }
    }, [invoices, markingFormData.invoice_id])

    const disabled = markingFormData.invoice_flag

    const sum =
        linkedLinks?.links.reduce((acc, link) => link.budget + acc, 0) ??
        analyticsTableRowType.budget

    const disabledCauseDeletedLink =
        !!markingFormData?.invoice_flag && isLinkDeleted

    return (
        <>
            <div className="flex flex-col gap-[6px]">
                <div className="flex w-[448px] justify-between">
                    <div className="flex items-center gap-[6px]">
                        <Icon
                            icon="invoice"
                            checked={markingFormData.invoice_flag}
                        />

                        <h2 className="text-sm font-semibold">Счет и оплата</h2>
                    </div>
                    {linkedLinksCount > 1 && (
                        <div className="flex items-center gap-[6px]">
                            <a
                                className="cursor-pointer text-xs text-black/40 underline"
                                onClick={openLinkedLinksModal}
                            >
                                Связанные элементы
                            </a>
                            <div className="relative flex h-4 w-4 items-center justify-center overflow-hidden rounded-[20px] bg-blue-primary text-[10px] text-white">
                                {linkedLinksCount}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-end justify-between">
                    <div className="flex w-[455px] items-center gap-2">
                        <div className="relative flex flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Сумма
                            </span>
                            <div>
                                <div className="flex items-center gap-[2px] rounded-[5px] border border-stroke px-[10px] py-2 text-xs">
                                    <span className="opacity-30">{sum}</span>
                                    <GreyRubleIconInline className="m-0" />
                                </div>
                            </div>
                        </div>

                        <div className="flex grow flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                При оплате на карту
                            </span>

                            <DisableTooltip
                                enabled={markingFormData.invoice_flag}
                                message="Если хотите отредактировать, то уберите галочку справа"
                            >
                                <Input2
                                    className={'h-[32px]'}
                                    disabled={disabled}
                                    value={accountDetailsValue}
                                    onChange={setAccountDetailsValue}
                                    placeholder="Введите номер карты"
                                    onBlur={onUpdateAccountDetails}
                                />
                            </DisableTooltip>
                        </div>

                        <div className="flex flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Счет
                            </span>

                            <DisableTooltip
                                enabled={markingFormData.invoice_flag}
                                message="Если хотите отредактировать, то уберите галочку справа"
                            >
                                <UploadFileFiled
                                    onDeleteItem={handleDelete}
                                    onFileChange={handleFileChange}
                                    disabled={disabled}
                                    placeholder="Загрузить счет"
                                    selectedItem={selectedItem}
                                    setSelectedItem={setSelectedItem}
                                    isLoading={uploadInvoiceFile.isPending}
                                    className="w-48"
                                />
                            </DisableTooltip>
                        </div>
                    </div>

                    <div className="flex w-[105px] items-center gap-[6px] pb-[5px]">
                        <DisableTooltip
                            enabled={disabledCauseDeletedLink}
                            className="h-5"
                            message={'Нельзя отжать так как ссылка удалена'}
                            asChild
                        >
                            <div
                                onClick={() => {
                                    if (!disabledCauseDeletedLink) {
                                        handleChangeCheckboxStatus()
                                    }
                                }}
                            >
                                <Checkbox
                                    id="invoice_allow"
                                    checked={markingFormData.invoice_flag}
                                    onChange={handleChangeCheckboxStatus}
                                    sizeVariant="big"
                                    variant="green"
                                    size={20}
                                    disabled={disabledCauseDeletedLink}
                                />
                            </div>
                        </DisableTooltip>
                        <Label
                            htmlFor="invoice_allow"
                            className="w-[79px] text-[10px] font-medium leading-3"
                        >
                            Счет оплачен
                        </Label>
                    </div>
                </div>
            </div>
        </>
    )
}
export default Invoice
